<script>
  import Button from '@leaflink/stash/Button.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Input from '@leaflink/stash/Input.vue';
  import { formatISO } from 'date-fns';

  import { env } from '@/constants/env';

  export default {
    name: 'EnvironmentDrawerLegacy',

    components: {
      Button,
      Icon,
      Input,
    },

    props: {
      buildInfo: {
        type: Object,
        default: () => ({}),
      },
    },

    data() {
      const prNumberCached = localStorage.getItem('mpfe_pr_number') || '';

      return {
        isOpen: false,
        devHost: 'localhost',
        devPort: 9006,
        prNumber: prNumberCached,
        prNumberCached,
        hintText: '',
        errorText: '',
      };
    },

    computed: {
      frontendEnv() {
        return this.isDevMode ? 'DEV' : env.VITE_RELEASE_VERSION;
      },

      isDevMode() {
        return env.MODE === 'development';
      },

      isPrMode() {
        return !!this.prNumberCached;
      },

      getPRUrl() {
        return `https://marketplace-web.dev.leaflink.io/prs/${this.prNumber}/PR-${this.prNumber}/`;
      },
    },

    watch: {
      prNumber() {
        this.hintText = '';
        this.errorText = '';
      },
    },

    methods: {
      formatISO,

      async fetchManifest() {
        if (!this.prNumber) {
          return;
        }

        const url = `${this.getPRUrl}entry-manifest.json`;

        try {
          const manifest = await fetch(url).then((response) => response.json());

          localStorage.setItem('mpfe_manifest', JSON.stringify(manifest));
          localStorage.setItem('mpfe_pr_number', this.prNumber);
          localStorage.setItem('mpfe_host', this.getPRUrl);
          localStorage.removeItem('mpfe_is_dev');

          this.hintText = 'PR Found! Reloading...';

          await new Promise((res) => setTimeout(res, 600));

          window.location.reload();
        } catch (error) {
          this.errorText = "Couldn't find that PR. Check the number and try again!";
        }
      },

      resetEnv() {
        localStorage.removeItem('mpfe_manifest');
        localStorage.removeItem('mpfe_pr_number');
        localStorage.removeItem('mpfe_is_dev');
        localStorage.removeItem('mpfe_host');

        window.location.reload();
      },

      useDevMode() {
        localStorage.removeItem('mpfe_manifest');
        localStorage.removeItem('mpfe_pr_number');

        const port = this.devPort ?? '9006';
        const devHost = this.devHost ?? 'localhost';
        const host = `//${devHost}:${port}`;

        localStorage.setItem('mpfe_host', host);
        localStorage.setItem('mpfe_is_dev', true);
        // this opens a localhost file so that you can accept the cert
        // More details about this can be found in this link https://github.com/LeafLink/marketplace-web?tab=readme-ov-file#getting-started
        window.open(`${host}/src/main.ts`);

        window.location.reload();
      },

      toggleDrawer() {
        this.isOpen = !this.isOpen;
      },
    },
  };
</script>

<template>
  <div data-test="env-drawer" :class="[$style['environment-drawer'], 'tw-w-full tw-bg-purple-500 tw-z-content']">
    <div
      :class="[
        $style.header,
        'tw-cursor-pointer heading-4 tw-rounded-t tw-p-3 text-uppercase tw-bg-orange-500 tw-flex tw-justify-between tw-items-center tw-text-ice-900',
      ]"
      @click="toggleDrawer"
    >
      <span :class="$style.env">
        <strong> BE: {{ buildInfo.envName }} / FE: {{ frontendEnv }} </strong>
      </span>
      <span>
        <Icon name="caret-down" :class="[isOpen ? '' : 'rotate-180', 'color-ice-900']" />
      </span>
    </div>
    <div :class="[isOpen ? 'tw-block' : 'tw-hidden', 'tw-p-2 tw-ice-ice tw-text-xs']">
      <Button v-if="isDevMode || isPrMode" class="!tw-mb-3" @click="resetEnv"> Reset </Button>

      <form v-if="!isDevMode" class="tw-flex tw-gap-x-1 tw-pb-3" @submit.prevent="useDevMode">
        <Input v-model="devHost" class="tw-w-full" />
        <Input v-model="devPort" class="tw-min-w-[60px] tw-max-w-[60px]" />
        <Button class="!tw-min-w-[48px]" type="submit" :disabled="!devHost || !devPort"> Dev </Button>
      </form>

      <form class="tw-flex tw-gap-x-1 tw-items-start tw-pb-3 !tw-text-xs" @submit.prevent="fetchManifest">
        <Input v-model="prNumber" :hint-text="hintText" :error-text="errorText" />
        <Button type="submit" :disabled="!prNumber"> Use open FE PR </Button>
      </form>

      <div class="tw-text-xs tw-text-white">
        <p>
          Build date (ISO):<br />
          {{ formatISO(buildInfo.buildDate * 1000) }}
        </p>

        <p>
          Git hash:<br />
          {{ buildInfo.gitHash }}
        </p>
        <p>
          Python:<br />
          {{ buildInfo.pythonVersion }}
        </p>
        <p class="tw-mb-0">
          Django:<br />
          {{ buildInfo.djangoVersion }}
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
  .environment-drawer {
    bottom: 0;
    position: absolute;
  }

  .header {
    height: 25px;
  }

  .content {
    min-height: 100px;
  }

  .env {
    line-height: typo('body', 'line-height');
  }
</style>
